import { FC, ReactElement, useEffect, useState } from "react";
import { TabButtonProps, TabProps, TabsElement, TabSetter, TabsProps } from "./types";
import Tab from "./Tab";
import DefaultTabButton from "./Button";

const Tabs:FC<TabsProps> = ({ 
  children, 
  defaultTab, 
  rootProps, 
  Button = DefaultTabButton, 
  buttonsSectionProps,
  ...props 
}) => {

  const { className: navClassName, ...navProps} = buttonsSectionProps ?? {};
  const { className: rootClassName = "", ...othersRootProps} = rootProps ?? {};

  const [activeTabIndex, setIndex] = useState(getInitialIndex(defaultTab, children));
  
  const setTab = (n: number) => {
    setIndex(n);
  }

  const [buttons, setButtons] = useState<TabButtonProps[]>(() => mapButtons(children, setTab, activeTabIndex));

  useEffect(() => {
    setButtons(mapButtons(children, setTab, activeTabIndex))
  }, [children, activeTabIndex])

  return ( 
    <div 
      className={`grid justify-items-center gap-10 w-full ${rootClassName}`} 
      {...othersRootProps}
    >
      <nav className={`flex space-x-2 ${navClassName}`} {...navProps}>
        {buttons.map((btn, i) => <Button key={`tb-btn-${i}`} {...btn}/>)}
      </nav>
      <div {...props}>
        {
          isArrayOfTabs(children) 
            ? children.map(
                (tab, index) => {
                  const {className, ...tabProps} = tab.props;
                  const isActive = index === activeTabIndex;
                  return (
                    <Tab 
                      key={`tab-${index}`}
                      className={`${isActive ? "" : "hidden"} 
                        ${className ?? ""}`
                      }
                      {...tabProps}
                    />
                  );
                }
              )
            : children
        }
      </div>
    </div>
  );
}

const isArrayOfTabs = (t: TabsElement): t is Array<ReactElement<TabProps>> => {
  return "length" in t;
}

const mapButtons = (tabs: TabsElement, setTab: TabSetter, activeTabIndex: number) => {
  if( isArrayOfTabs(tabs) ) {
    return ( 
      tabs.map( (tab, i) => {
        return ({
          callback: () => {setTab(i)},
          isActive: i === activeTabIndex,
          label: tab.props.label,
        })
      }) 
    );
  } else {
    return ( 
      [{
        callback: () => {setTab(0)},
        isActive: true,
        label: tabs.props.label,
      }]
    );
  }
}

const getInitialIndex = (defaultValue: string | number | undefined, tabs: TabsElement): number => {

  const check = (index: number) => {
    return isArrayOfTabs(tabs) 
      ? (tabs.length > index) && (0 <= index)
      : index === 0;
  }
  const valueOrZero = (value: number, check: (n: number) => boolean) => {
    return check(value) ? value : 0;
  }

  switch (true) {
    case typeof defaultValue === "number":
      return valueOrZero(defaultValue as number, check);
    case (typeof defaultValue === "string"):
      return valueOrZero (
        isArrayOfTabs(tabs) 
          ? tabs.findIndex((e) => e.props.label === defaultValue) 
          : tabs.props.label === defaultValue ? 0 : -1,
        check
      )
    default: 
      return 0;
  }
}

export default Tabs;
